import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { catchError, of, retry, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { StorageService } from './storage-service.service';
import { isPlatformBrowser } from '@angular/common';
@Injectable({
  providedIn: 'root'
})
export class LoadTranslationService {
  /**
   * Variable used to store environment details
   */
  environment!: { [key: string]: string };
  /**
   * variable that holds the translated values
   */
  translationObj: { [key: string]: any } = {};
  /**
   * Stores the store language code
   */
  languageCode!: string;
  /**
   * Variable that stores the store language list
   */
  languageArray!: { langCode: string, langName: string }[];
  /**
   * Variable that is used for navigation
   */
  isNavigateWithLangCode!: boolean;
  /**
   * Variable that is used to store the translation path
   */
  translationFolder: string[] = [];
  /**
   * Variable used to store the existing JSON's language
   */
  existingLangCode!: string;
  /**
   * service constructor which is used to load translation file
   * @param environment to get the variables of storefront environment
   * @param httpRoutingService to use the functions in httpRoutingService Service.
   */
  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject('REQUEST') private request: any,
    @Optional() @Inject('RESPONSE') private response: any
  ) { }
  /**
   * Method whcih is used to set the `lang` cookie for the current language and load the corresponding translation file.
   * @param code The language code to set in the cookie (e.g., 'en', 'fr').
   */
  setLangCookie(code: string) {
    if (this.response) {
      this.response.setHeader('Set-Cookie', `lang=${code}; Path=/; Secure; SameSite=Lax`);
      const fs = (globalThis as any)['require']?.('fs');
      const path = (globalThis as any)['require']?.('path');
      const { fileURLToPath } = (globalThis as any)['require']?.('url');
      if (!fs || !path || !fileURLToPath) {
        console.error('Server-side file system modules are not available.');
        return;
      }
      // Get the server-side dist folder
      const serverDistFolder = path.dirname(fileURLToPath(import.meta.url));
      const browserDistFolder = path.resolve(serverDistFolder, '../browser');
      // Construct the file path
      const filePath = path.join(browserDistFolder, 'assets/i18n', `${code}.json`);
      // Read and parse JSON file
      const jsonData = fs.readFileSync(filePath, 'utf-8');
      (globalThis as any).translationObj = JSON.parse(jsonData);
    }
  }
  /**
   * Method used to checks whether the code is running on the server or the browser
   * and extracts the `lang` cookie value accordingly.
   * @param isServer A boolean indicating whether the code is running on the server.
   */
  getLangFromCookie(isServer: boolean): string | undefined {
    if (isServer) {
      return this.request && this.request.headers && this.request.headers.cookie ?
        this.request.headers.cookie.split('; ').find((row: any) => row.startsWith('lang='))?.split('=')[1] : undefined;
    } else {
      const langCookie = document.cookie.split('; ').find((row) => row.startsWith('lang='));
      return langCookie ? langCookie.split('=')[1] : undefined;
    }
  }
  /**
   * Method to load the combine translated JSON value
   * @param langCode language to be loaded
   */
  loadTranslationFile(langCode: string) {
    if (this.existingLangCode !== langCode) {
      this.existingLangCode = langCode;
      const translationPath = './assets/i18n/' + langCode + '.json';
      if (isPlatformBrowser(this.platformId)) {
        this.http.get(translationPath).pipe(
          catchError((error) => {
            console.log('check error', error);
            if (error.status === 0 && error instanceof HttpErrorResponse) {
              // If it's any network error, including CORS, do not retry
              return throwError(() => new Error("Network Error")); // Throw the error without retrying
            }
            if (langCode == 'en') {
              const retryApiPath = './assets/i18n/en.json';
              return this.http.get(retryApiPath).pipe(retry(1));
            }
            return of(false);
          })
        ).subscribe({
          next: (translations) => {
            if (translations) {
              (globalThis as any).translationObj = translations;
            }
          },
          error: (error) => {
            console.error('Error loading translations', error);
          }
        });
      }
    }
  }
  /**
    * Method which is used to load multiple translation file 
    * @param files filepath
    */
  multiLoadTranslation(files: string[]): Promise<any> {
    if (files && files.length) {
      const promise = files.map(file => {
        const value = file.toUpperCase();
        return this.loadTranslationFiles(value);
      });
      return Promise.all(promise)
    }
    return Promise.resolve()
  }
  /**
   * Method which is used load translations
   * @param files filepath
   * @param lang language
   */
  loadTranslationFiles(file: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const lang = this.getLanguageCode();
      const folderName = file.toLowerCase();
      if (!this.translationFolder.includes(folderName)) {
        this.translationFolder.push(folderName);
        const translationPath = './assets/i18n/' + folderName + '/' + lang + '.json';
        // const translationPath = this.environment['awsImageUrl'] + 'i18n/' + folderName + '/' + lang + '.json';
        this.http.get(translationPath).pipe(
          catchError((error) => {
            if (error.status === 0 && error instanceof HttpErrorResponse) {
              // If it's any network error, including CORS, do not retry
              return throwError(() => new Error("Network Error")); // Throw the error without retrying
            }
            if (lang !== 'en') {
              const retryApiPath = './assets/i18n/' + folderName + '/en.json';
              // const retryApiPath = this.environment?.awsImageUrl + 'i18n/' + folderName + '/en.json';
              return this.http.get(retryApiPath).pipe(retry(1));
            }
            return of(false);
          })
        ).subscribe({
          next: (translations) => {
            if (translations) {
              (globalThis as any).translationObj[file] = translations;
            }
            resolve(translations); // Resolve the promise with the translations
          },
          error: (error) => {
            console.error('Error loading translations', error);
            reject(error); // Reject the promise if there's an error
          }
        });
      } else {
        // If the folder is already included, resolve immediately with the existing translation
        resolve((globalThis as any).translationObj[file]);
      }
    });
  }
  /**
   * Method used to get the language code from the local storage
   * @returns language code
   */
  private getLanguageCode(): string {
    const langCode = typeof window !== 'undefined' ? this.storageService.getItem('language') ?? "en" : "en";
    return langCode;
  }
  /**
   * method to change the translation object when the language changes
   */
  changeTranslationObj(): void {
    this.loadTranslationFile(this.languageCode ?? 'en');
  }


  getTranslation(key: string) {
    const keys = key?.includes('.') ? key.split('.') : [];
    const translationObj = (globalThis as any).translationObj;
    const value = (translationObj && keys[0] && translationObj[keys[0]]) ? translationObj[keys[0]] : null;
    if (value !== undefined && value !== null) {
      return keys[1] ? value[keys[1]] ?? value : value;
    }
    return key;
  }

  rephraseTranslation(key: string, values: any): string | null {
    let msg: string;
    if (key) {
      msg = this.getTranslation(key);
      if (msg && values && values.length) {
        values.forEach((res: any, index: number) => msg = msg.replace('${' + index + '}', res));
      }
      return msg;
    }
    return null;
  }
}
